import styled from "styled-components";
import { FaMicrophone, FaHandshake, FaBook, FaBullhorn, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useState, useEffect, useRef } from "react";

const addresses = [
  {
    icon: <FaMicrophone />,
    label: "Welcome Address & IIT Madras Vision",
    speaker: "Prof. Ashwin Mahalingam",
    designation: "Dean, Alumni & Corporate Relations, IIT Madras",
  },
  {
    icon: <FaMicrophone />,
    label: "Address",
    speaker: "Prof. V. Kamakoti",
    designation: "Director, IIT Madras",
  },
  {
    icon: <FaMicrophone />,
    label: "Address by Guest of Honour",
    speaker: "Shri Jayant Chaudhary",
    designation: "Union Minister of State for Education and Minister of State for Skill Development & Entrepreneurship (Independent Charge), Government of India",
    badge: "Guest of Honour",
  },
  {
    icon: <FaMicrophone />,
    label: "Address by Chief Guest",
    speaker: "Shri Dharmendra Pradhan",
    designation: "Hon'ble Minister of Education, Government of India",
    badge: "Chief Guest",
  },
];

const presenceList = [];

const partnerships = [
  {
    org: "NTPC",
    title: "Centre for Cardiovascular Research",
  },
  {
    org: "BPCL",
    title: "Centre for NextGen Technologies for Robotic Assisted Surgeries",
    subtitle: "YANTRAS",
  },
  {
    org: "HSBC",
    title: "Centre for Resource Efficiency, Recyclability & Circularity in Energy Transition",
  },
];

const announcements = [
  {
    icon: <FaBullhorn />,
    text: "Bodhan AI",
    type: "Announcement",
  },
  {
    icon: <FaBook />,
    text: "Inventing a Better Tomorrow: 25 Years of IIT Madras Impact",
    type: "IIT Madras Book Launch",
  },
];

const addressCarouselImages = [
  require("../assets/images/summit/inagural1.webp"),
  require("../assets/images/summit/inagural2.webp"),
  require("../assets/images/summit/inagural9.webp"),
  require("../assets/images/summit/inagural10.webp"),
  require("../assets/images/summit/inagural11.webp"),
  require("../assets/images/summit/inagural12.webp"),
];
const partnerCarouselImages = [
  require("../assets/images/summit/inagural5.webp"),
  require("../assets/images/summit/inagural4.webp"),
  require("../assets/images/summit/inagural3.webp"),
];

const carouselImages = [
  require("../assets/images/summit/bodhanai1.webp"),
  require("../assets/images/summit/inagural7.webp"),
];

const Carousel = ({ images, label }) => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const total = images.length;

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  useEffect(() => {
    if (total < 2) return;
    timerRef.current = setInterval(() => {
      setCurrent((c) => (c + 1) % total);
    }, 3500);
    return () => clearInterval(timerRef.current);
  }, [total]);

  return (
    <CarouselWrap>
      <CarouselTrack>
        {total > 0 ? (
          <CarouselImg src={images[current]} alt={`Inaugural ${current + 1}`} />
        ) : (
          <CarouselPlaceholder>
            <PlaceholderText>Images coming soon</PlaceholderText>
          </CarouselPlaceholder>
        )}
        {label && <CarouselOverlayLabel>{label}</CarouselOverlayLabel>}
      </CarouselTrack>
      <CarouselControls>
        <CtrlBtn onClick={prev}><FaChevronLeft /></CtrlBtn>
        <Dots>
          {total > 0
            ? images.map((_, i) => (
                <Dot key={i} active={i === current} onClick={() => setCurrent(i)} />
              ))
            : Array.from({ length: 10 }).map((_, i) => (
                <Dot key={i} active={i === 0} />
              ))
          }
        </Dots>
        <CtrlBtn onClick={next}><FaChevronRight /></CtrlBtn>
      </CarouselControls>
    </CarouselWrap>
  );
};

const InauguralSection = () => {
  return (
    <Section id="inaugural-session">
      <SectionLabel>Programme Highlights</SectionLabel>
      <MainHeading>Inaugural Session</MainHeading>
      <Divider />
      <Introduction>
        Union Education Minister Shri Dharmendra Pradhan inaugurated IIT Madras' first-ever
        Technology Summit at Bharat Mandapam, New Delhi. Shri Jayant Chaudhary (MoS, Skill
        Development &amp; Entrepreneurship) was Guest of Honour. Director Prof. V. Kamakoti and
        Dean (ACR) Prof. Ashwin Mahalingam welcomed delegates and laid out IITM's vision for
        industry–academia–government partnership in service of Viksit Bharat 2047.
      </Introduction>

      <Wrapper>

        {/* ── Addresses ── */}
        <BlockTitle>
          <BlockIcon><FaMicrophone /></BlockIcon>
          Addresses
        </BlockTitle>

        <AddressSplitLayout>
          <AddressCardsCol>
            {addresses.map((item, i) => (
              <AddressCard key={i}>
                <CardIndex>{String(i + 1).padStart(2, "0")}</CardIndex>
                <CardBody>
                  <CardLabel>{item.label}</CardLabel>
                  <SpeakerRow>
                    <CardSpeaker>{item.speaker}</CardSpeaker>
                    {item.badge && <Badge>{item.badge}</Badge>}
                  </SpeakerRow>
                  {item.designation && <CardDesignation>{item.designation}</CardDesignation>}
                </CardBody>
              </AddressCard>
            ))}
          </AddressCardsCol>
          <AddressCarouselCol>
            <GalleryCard>
              <Carousel images={addressCarouselImages} label="Address Gallery" />
            </GalleryCard>
          </AddressCarouselCol>
        </AddressSplitLayout>

        {/* ── Partnership Launch ── */}
        <PartnershipBlock>
          <BlockTitle>
            <BlockIcon><FaHandshake /></BlockIcon>
            Centre Launch Announcements
          </BlockTitle>
          <PresenceNote>
            Launched by the <strong>Chief Guest</strong> in the presence of:
          </PresenceNote>
          <PresenceList>
            {presenceList.map((name, i) => (
              <PresencePill key={i}>{name}</PresencePill>
            ))}
          </PresenceList>

          <PartnerSplitLayout>
            <PartnerGrid>
              {partnerships.map((p, i) => (
                <PartnerCard key={i}>
                  <OrgBadge>{p.org}</OrgBadge>
                  <PartnerTitle>{p.title}</PartnerTitle>
                  {p.subtitle && <PartnerSubtitle>{p.subtitle}</PartnerSubtitle>}
                </PartnerCard>
              ))}
            </PartnerGrid>
            <GalleryCard>
              <Carousel images={partnerCarouselImages} label="Partnership Gallery" />
            </GalleryCard>
          </PartnerSplitLayout>
        </PartnershipBlock>

        {/* ── Announcements ── */}
        <BlockTitle style={{ marginTop: "40px" }}>
          <BlockIcon><FaBullhorn /></BlockIcon>
          Announcements & Launches
        </BlockTitle>

        <AnnLayout>
          {/* Left: cards */}
          <AnnCards>
            {announcements.map((a, i) => (
              <AnnouncementCard key={i}>
                <AnnIcon>{a.icon}</AnnIcon>
                <AnnBody>
                  <AnnType>{a.type}</AnnType>
                  <AnnText>{a.text}</AnnText>
                </AnnBody>
              </AnnouncementCard>
            ))}
          </AnnCards>

          {/* Right: image carousel */}
          <GalleryCard>
            <Carousel images={carouselImages} label="Inaugural Gallery" />
          </GalleryCard>
        </AnnLayout>

      </Wrapper>
    </Section>
  );
};

export default InauguralSection;


/* ================= STYLES ================= */

const Section = styled.section`
  padding: 40px 20px;
  background: #faf8f5;

  @media (max-width: 768px) {
    padding: 36px 14px;
  }
`;

const SectionLabel = styled.p`
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #d6a54f;
  margin-bottom: 10px;
`;

const MainHeading = styled.h2`
  text-align: center;
  font-size: 38px;
  font-weight: 700;
  color: #791e1b;
  margin: 0 0 4px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Introduction = styled.p`
  text-align: center;
  max-width: 860px;
  margin: 0 auto 20px;
  font-size: 15px;
  line-height: 1.9;
  color: #4b5563;

  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 28px;
  }
`;

const Divider = styled.div`
  width: 60px;
  height: 4px;
  background: #d6a54f;
  border-radius: 2px;
  margin: 0 auto 18px;
`;

const Wrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const BlockTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 700;
  color: #171f32;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BlockIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #791e1b;
  color: #fff;
  font-size: 14px;
  flex-shrink: 0;
`;

/* ── Address cards ── */

const AddressSplitLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 38px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AddressCardsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AddressCarouselCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddressCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.07);
  padding: 14px 18px;
  border-left: 4px solid #791e1b;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.12);
  }
`;

const CardIndex = styled.span`
  font-size: 28px;
  font-weight: 800;
  color: #e8d5b0;
  line-height: 1;
  flex-shrink: 0;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SpeakerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const CardLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CardDesignation = styled.span`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
`;

const CardSpeaker = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: #171f32;
`;

const Badge = styled.span`
  display: inline-block;
  background: #791e1b;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 20px;
  margin-top: 4px;
`;

/* ── Partnership block ── */

const PartnershipBlock = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 36px 32px;
  box-shadow: 0 4px 22px rgba(0, 0, 0, 0.07);
  margin-bottom: 12px;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const PresenceNote = styled.p`
  font-size: 15px;
  color: #4b5563;
  margin-bottom: 4px;
`;

const PresenceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 30px;
`;

const PresencePill = styled.span`
  background: #fef3e2;
  color: #92600a;
  border: 1px solid #f0c870;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 20px;
`;

const PartnerSplitLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PartnerGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PartnerCard = styled.div`
  background: #faf8f5;
  border-radius: 12px;
  padding: 20px 18px;
  border-top: 3px solid #d6a54f;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OrgBadge = styled.span`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #791e1b;
  background: #f9e8e8;
  border-radius: 6px;
  padding: 3px 10px;
  align-self: flex-start;
`;

const PartnerTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #171f32;
  line-height: 1.5;
  margin: 0;
`;

const PartnerSubtitle = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: #d6a54f;
  margin: 0;
`;

/* ── Announcements ── */

const AnnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 16px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AnnCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const AnnouncementCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #171f32;
  color: #fff;
  border-radius: 14px;
  padding: 22px 24px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.13);
  transition: transform 0.2s ease;
  flex: 1;

  &:hover {
    transform: translateY(-4px);
  }
`;

const AnnIcon = styled.span`
  font-size: 22px;
  color: #d6a54f;
  flex-shrink: 0;
  margin-top: 2px;
`;

const AnnBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const AnnType = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #d6a54f;
`;

const AnnText = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #fff;
`;

/* ── Gallery card ── */

const GalleryCard = styled.div`
  background: #171f32;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 4px 22px rgba(0, 0, 0, 0.13);
  display: flex;
  flex-direction: column;
`;


/* ── Carousel ── */

const CarouselWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CarouselTrack = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  min-height: 260px;
`;

const CarouselImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CarouselOverlayLabel = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(23, 31, 50, 0.7);
  backdrop-filter: blur(4px);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const CarouselPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  background: repeating-linear-gradient(
    135deg,
    #1e2740 0px,
    #1e2740 18px,
    #171f32 18px,
    #171f32 36px
  );
`;

const PlaceholderText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #d6a54f;
  letter-spacing: 1px;
  opacity: 0.8;
`;

const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px;
  background: #171f32;
`;

const CtrlBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.button`
  width: ${(p) => (p.active ? "20px" : "8px")};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${(p) => (p.active ? "#ffffff" : "rgba(255,255,255,0.3)")};
  cursor: pointer;
  transition: width 0.3s ease, background 0.3s ease;
  padding: 0;
`;
